import { ulid } from 'ulid';
import { omit, isNil, range, shuffle, sampleSize, pick } from 'lodash';
import statistics from '@nsftx/games-sdk-js/src/api/statistics';
import results from '@nsftx/games-sdk-js/src/api/results';
import auth from '@nsftx/games-sdk-js/src/api/auth';
import eventBus from '@nsftx/games-sdk-js/src/utility/eventBus';
import { ticketFormatter } from '@/utility';
import api from '@/api';
import types from './mutationTypes';

export default {
  createSystems({ state, commit, getters }) {
    const { selectedNumbersLength } = getters;
    const { paytable } = state;

    if (selectedNumbersLength === 0) {
      commit(types.SET_SUGGESTED_SYSTEMS, []);

      return;
    }

    const parseNumbersFromKey = (key) => key.split('_').map(Number);

    const filterSuggestedSystem = (key) => {
      const [firstNumber, lastNumber] = parseNumbersFromKey(key, true);

      if (
        firstNumber !== lastNumber &&
        selectedNumbersLength === lastNumber &&
        selectedNumbersLength >= firstNumber
      ) {
        return true;
      }

      return false;
    };

    const suggestedSystemKeys = Object.keys(paytable).filter(
      filterSuggestedSystem,
    );

    commit(types.SET_SUGGESTED_SYSTEMS, pick(paytable, suggestedSystemKeys));
  },
  checkTicket({ state, getters, dispatch }, requestUuid) {
    const timeoutId = setTimeout(async () => {
      const { checkTicketTimeoutId } = state;

      if (checkTicketTimeoutId) {
        clearTimeout(checkTicketTimeoutId);
      }

      try {
        // TODO: Change to gamesBetslip ticketCheck
        const { data } = await dispatch(
          'gamesBetslip/ticketCheck',
          requestUuid,
        );

        if (data.status.value.toUpperCase() !== 'PENDING') {
          if (!getters.isTerminalChannel) {
            dispatch('gamesBetslip/getLastTickets');
            // Dispatch clearBetslip before setNotification
            // Because clearBetslip clears the notifications
            dispatch('gamesBetslip/clearBetslip');
            dispatch('notifications/setNotification', {
              message: getters.translations.general_ticket_accepted,
              status: 'positive',
              notificationTimeout: 3000,
            });
            dispatch('gamesBetslip/addPreBet', {
              bet: '',
              valid: false,
            });
          } else {
            dispatch('gamesBetslip/clearBetslip');
            eventBus.$emit('HandleTerminalTicketCheck', data);
          }
        }
      } catch (error) {
        const message = error?.response?.data?.message ?? 'unknown_error';

        if (getters.isTerminalChannel) {
          eventBus.$emit('UpdateTicketOnError', requestUuid);
        }
        dispatch('notifications/setNotification', {
          message,
          status: 'negative',
          notificationTimeout: null,
        });
      } finally {
        dispatch('setIsPayinInProcess', false);
        dispatch('gamesBetslip/setIsPayinInProgress', false);
      }
    }, 10000);

    dispatch('setTicketCheckTimeoutId', timeoutId);
  },
  async getPaytable({ getters, dispatch }) {
    const { config } = getters;

    try {
      const response = await api.paytable.getPaytable(config);

      dispatch('setPaytable', response);
    } catch (error) {
      const message = error?.response?.data?.message ?? 'unknown_error';

      dispatch('notifications/setNotification', {
        message,
        status: 'negative',
        notificationTimeout: null,
      });
    }
  },
  // eslint-disable-next-line no-unused-vars
  formatPlayerTickets(_, tickets) {
    return tickets?.map(ticketFormatter);
  },
  async getPlayer({ getters, dispatch }, { config, token }) {
    try {
      const response = await auth.authenticate(config, token);

      if (!isNil(response)) {
        dispatch('user/updateUser', {
          auth: {
            token: response.auth.token,
            tpToken: response.auth.thirdPartyToken || null,
          },
          profile: {
            ...omit(response, ['auth', 'balance']),
            logged: true,
            id: response.uuid,
          },
          isLoggedIn: true,
        });
        dispatch('user/setUserBalance', response.balance);

        setTimeout(() => {
          eventBus.$emit('Keno:AuthorizationChanged', {
            isUserLoggedIn: true,
            userUuid: getters.user.profile.id,
          });
        }, 500);
      }
    } catch (error) {
      eventBus.$emit('Keno:AuthorizationChanged', {
        isUserLoggedIn: false,
        userUuid: null,
      });

      const message = error?.response?.data?.message ?? 'unknown_error';

      dispatch('notifications/setNotification', {
        message,
        status: 'negative',
        notificationTimeout: null,
      });
    }
  },
  async getStatistics({ state, dispatch, commit }) {
    try {
      commit(types.SET_IS_STATISTICS_LOADING, true);

      const response = await statistics.getStatistics(state.config);

      commit(types.SET_STATISTICS, response.data);
    } catch (error) {
      const message = error?.response?.data?.message ?? 'unknown_error';

      dispatch('notifications/setNotification', {
        message,
        status: 'negative',
        notificationTimeout: null,
      });
    } finally {
      commit(types.SET_IS_STATISTICS_LOADING, false);
    }
  },
  async getTicketDetails(
    { dispatch, state },
    { terminalUserData, id, busServiceCallback },
  ) {
    try {
      const response = await api.ticketCheck.getTicketCheck(
        terminalUserData,
        id,
        state.config,
      );

      dispatch('setTerminalTicketDetails', response);
    } catch (error) {
      const message = error?.response?.data?.message ?? 'unknown_error';

      busServiceCallback.sendMessage(
        {
          action: 'Dialog.Show',
          data: {
            message: this.getTranslation(message),
            type: 'warning',
            delay: 3000,
          },
        },
        true,
      );
    }
  },
  async getResults({ state, dispatch, commit }) {
    try {
      commit(types.SET_IS_RESULTS_LOADING, true);

      const response = await results.getResults(state.config, null);

      commit(types.SET_RESULTS, response.data);
    } catch (error) {
      const message = error?.response?.data?.message ?? 'unknown_error';

      dispatch('notifications/setNotification', {
        message,
        status: 'negative',
        notificationTimeout: null,
      });
    } finally {
      commit(types.SET_IS_RESULTS_LOADING, false);
    }
  },
  async setJackpotState({ commit, state }) {
    const jackpot = await api.ajs.getJackpot(state.config);

    if (jackpot.jackpotId) {
      commit(types.SET_JACKPOT_STATE, jackpot);
    }
  },
  // eslint-disable-next-line consistent-return
  async terminalPayIn({ dispatch, rootGetters }) {
    const formatBets = (ticket) =>
      ticket.map((bet) => ({
        type: bet.type,
        value: bet.value,
        system: bet.system,
        numEvents: bet.numEvents ?? 1,
        payin: Number(
          Number(bet.stake ?? rootGetters['gamesBetslip/totalPayment']).toFixed(
            2,
          ),
        ),
      }));

    try {
      dispatch('setIsPayinInProcess', true);
      dispatch('gamesBetslip/setIsPayinInProgress', true);

      const data = await dispatch('gamesBetslip/payIn', {
        payload: formatBets,
      });

      return data;
    } catch (error) {
      const message = error?.response?.data?.message ?? 'unknown_error';

      dispatch('notifications/setNotification', {
        message,
        status: 'negative',
        notificationTimeout: null,
      });

      dispatch('setIsPayinInProcess', false);
      dispatch('gamesBetslip/setIsPayinInProgress', false);
    }
  },
  async payIn({ state, dispatch, rootGetters, getters }) {
    const formatBets = (ticket) =>
      ticket.map((bet) => ({
        type: bet.type,
        value: bet.value,
        system: bet.system,
        numEvents: bet.numEvents ?? 1,
        payin: Number(
          Number(bet.stake ?? rootGetters['gamesBetslip/totalPayment']).toFixed(
            2,
          ),
        ),
      }));

    try {
      dispatch('setIsPayinInProcess', true);
      dispatch('gamesBetslip/setIsPayinInProgress', true);
      dispatch('notifications/setNotification', {
        local: 'general_ticket_resolving',
        status: 'neutral',
      });

      const { data } = await dispatch('gamesBetslip/payIn', {
        payload: formatBets,
        providerBetslipId: ulid() ?? null,
      }).then(async (response) => {
        if (getters.isNextGen && response.status === 201) {
          dispatch('gamesBetslip/setIsPayinInProgress', false);
          dispatch('gamesBetslip/getLastTickets');
          dispatch('gamesBetslip/addPreBet', {
            bet: '',
            valid: false,
          });
          dispatch('setIsPayinInProcess', false);
          dispatch('setCurrentBetType', { label: 'No Bet ' });
          dispatch('setSelectedNumbers', []);

          const message = {
            local: 'general_ticket_confirmed',
            status: 'positive',
            notificationTimeout: 3000,
          };

          dispatch('notifications/clearNotification').then(() => {
            dispatch('notifications/setNotification', message);
          });
          dispatch('user/getUserFreeBets');
          setTimeout(async () => {
            if (state.nextgenStandaloneIntegration) {
              await window.igniteInstance.getWallets().then((result) => {
                if (result.activeCurrencyBalance) {
                  dispatch('setUserBalance', result.activeCurrencyBalance);
                }
              });
            }
          }, 1000);
        }

        dispatch('gamesBetslip/clearBetslip');
        return response;
      });

      if (getters.isSevenPlatform) {
        dispatch('checkTicket', data.requestUuid);
      }
    } catch (error) {
      const message = error?.response?.data?.message ?? 'unknown_error';

      dispatch('notifications/setNotification', {
        message,
        status: 'negative',
        notificationTimeout: null,
      });

      dispatch('setIsPayinInProcess', false);
      dispatch('gamesBetslip/setIsPayinInProgress', false);
    }
  },
  randomNumbers({ commit, state }, length) {
    const { numbers } = state;
    const randomNumbers = sampleSize(
      shuffle(range(numbers.min, numbers.max)),
      length,
    ).map((number) => ({
      value: String(number),
    }));

    commit(types.SET_SELECTED_NUMBERS, randomNumbers);
  },
  setUserUuid({ commit }, userUuid) {
    commit(types.SET_USER_UUID, userUuid);
  },
  setCancelMessage({ commit, getters }, ticketId) {
    const message = `${getters.translations.general_ticket} ${ticketId} ${getters.translations.general_successfully_cancelled}`;
    commit(types.SET_CANCEL_MESSAGE, message);
  },
  setGameState({ commit }, gameState) {
    commit(types.SET_GAME_STATE, gameState);
  },
  setConfig({ commit }, config) {
    commit(types.SET_CONFIG, config);
  },
  setIsMobile({ commit }, isMobile) {
    commit(types.SET_IS_MOBILE, isMobile);
  },
  setDeliveryPlatform({ commit }, deliveryPlatform) {
    commit(types.SET_DELIVERY_PLATFORM, deliveryPlatform);
  },
  setSelectedNumbers({ commit }, selectedNumbers) {
    commit(types.SET_SELECTED_NUMBERS, selectedNumbers);
  },
  setQuickpickNumbersItem({ commit }, item) {
    commit(types.SET_QUICKPICK_NUMBERS_ITEM, item);
  },
  setQuickpickSystemsItem({ commit }, item) {
    commit(types.SET_QUICKPICK_SYSTEMS_ITEM, item);
  },
  setOdds({ commit }, odds) {
    commit(types.SET_ODDS, odds);
  },
  setPaytable({ commit }, paytable) {
    commit(types.SET_PAYTABLE, paytable);
  },
  setCurrentBetType({ commit }, currentBetType) {
    commit(types.SET_CURRENT_BET_TYPE, currentBetType);
  },
  setIsNoDrawAuto({ commit }, isNoDrawAuto) {
    commit(types.SET_IS_NO_DRAW_AUTO, isNoDrawAuto);
  },
  setCurrentSpecialBet({ commit }, currentSpecialBet) {
    commit(types.SET_CURRENT_SPECIAL_BET, currentSpecialBet);
  },
  setIsGameInfoOpen({ commit }, isGameInfoOpen) {
    commit(types.SET_IS_GAME_INFO_OPEN, isGameInfoOpen);
  },
  setIsSettingsOpen({ commit }, isSettingsOpen) {
    commit(types.SET_IS_SETTINGS_OPEN, isSettingsOpen);
  },
  setIsPayinInProcess({ commit }, isPayinInProcess) {
    commit(types.SET_IS_PAYIN_IN_PROCESS, isPayinInProcess);
  },
  setIsBetslipOpen({ commit }, isBetslipOpen) {
    commit(types.SET_IS_BETSLIP_OPEN, isBetslipOpen);
  },
  setIsTicketHistoryOpen({ commit }, isTicketHistoryOpen) {
    commit(types.SET_IS_TICKET_HISTORY_OPEN, isTicketHistoryOpen);
  },
  setIsCancelMessageOpen({ commit }, isCancelMessageOpen) {
    commit(types.SET_IS_CANCEL_MESSAGE_OPEN, isCancelMessageOpen);
  },
  setTicketCheckTimeoutId({ commit }, ticketCheckTimeoutId) {
    commit(types.SET_CHECK_TICKET_TIMEOUT_ID, ticketCheckTimeoutId);
  },
  setLastSystemSelection({ commit }, lastSystem) {
    commit(types.SET_LAST_SYSTEM_SELECTION, lastSystem);
  },
  setVisualizationState({ commit }, visualizationState) {
    commit(types.SET_VISUALIZATION_STATE, visualizationState);
  },
  setLayout({ commit }, layout) {
    commit(types.SET_LAYOUT, layout);
  },
  setTerminalUserData({ commit }, terminalUserData) {
    commit(types.SET_TERMINAL_USER_DATA, terminalUserData);
  },
  setTerminalTicketsHistory({ commit }, ticketsHistory) {
    commit(types.SET_TERMINAL_TICKETS_HISTORY, ticketsHistory);
  },
  setTerminalTicketDetails({ commit }, ticketDetails) {
    commit(types.SET_TERMINAL_TICKET_DETAILS, ticketDetails);
  },
  setNextRoundDate({ commit }, nextRoundDate) {
    commit(types.SET_NEXT_ROUND_DATE, nextRoundDate);
  },
  setUserBalance({ dispatch, state }, payload) {
    const divider = state.config?.ui?.config?.balanceDivider;
    const balance = divider ? payload / divider : payload;
    dispatch('user/setUserBalance', balance, { root: true });
  },
  clearCancelMessage({ commit }) {
    commit(types.CLEAR_CANCEL_MESSAGE);
  },
  toggleNoDraw({ commit }) {
    commit(types.TOGGLE_NO_DRAW);
  },
  setNoDraw({ commit }, isActive) {
    commit(types.SET_NO_DRAW, isActive);
  },
  toggleAllIn({ commit }) {
    commit(types.TOGGLE_ALL_IN);
  },
  setTicketToRebet({ commit }, ticketToRebet) {
    commit(types.SET_TICKET_TO_REBET, ticketToRebet);
  },
  setActiveComponent({ commit }, activeComponent) {
    commit(types.SET_ACTIVE_COMPONENT, activeComponent);
  },
  setBettingDisabled({ commit }, bettingDisabled) {
    commit(types.SET_BETTING_DISABLED, bettingDisabled);
  },
  setHeaderIconsEnabled({ commit }, enabled) {
    commit(types.SET_HEADER_ICONS_ENABLED, enabled);
  },
  updateConfig({ commit }, property) {
    commit(types.UPDATE_CONFIG, property);
  },
  setNextGenConfig({ commit }, config) {
    commit(types.SET_NEXT_GEN_CONFIG, config);
  },
  updateConfigWithNextGenConfig({ state, commit }) {
    const { nextGenConfig } = state;
    if (!nextGenConfig) return;
    Object.entries(nextGenConfig).forEach(([key, value]) => {
      commit(types.UPDATE_CONFIG, { [key]: value });
    });
  },
  toggleFreeBetModal({ commit }, show) {
    commit(types.TOGGLE_FREE_BET_MODAL, show);
  },
  toggleFreeBetExpiredModal({ commit }, show) {
    commit(types.TOGGLE_FREE_BET_EXPIRED_MODAL, show);
  },
  setDrawnBall({ commit }, ball) {
    commit(types.SET_DRAWN_BALL, ball);
  },
  setDrawnBalls({ commit }, data) {
    commit(types.SET_DRAWN_BALLS, data);
  },
  setVisualizationLoaded({ commit }, isLoaded) {
    commit(types.SET_VISUALIZATION_LOADED, isLoaded);
  },
  setNextGenStandaloneIntegration({ commit }, standaloneIntegration) {
    commit(types.SET_NEXTGEN_STANDALONE_INTEGRATION, standaloneIntegration);
  },
};
